import { useEffect, useRef, useState } from 'react'
import { useSearch } from '@tanstack/react-router'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@campai/lib-web/routes'
import { client, type RouterOutput } from '@campai/lib-web/utils/trpc'

import { Page } from '@/components/Page'
import { type AttachmentInput } from '@/product/community/shared/Attachments'
import { canCreateFeedbackContent } from '@/product/feedback/access'
import { TAG_FILTER_ALL, TAG_FILTER_NONE } from '@/product/feedback/tagFilter'
import { type Status } from '@/product/feedback/status'
import { TicketDialog } from '@/product/feedback/shared/TicketDialog'
import { getTicketSort, pageSize, type Search } from '@/product/feedback/home/routeUtils'

import { Toolbar } from './Toolbar'
import { TicketList } from './TicketList'

type TicketListItem = RouterOutput['product']['feedback']['listTickets']['tickets'][number]

export const Home = observer(() => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const listSearch = useSearch({ from: '/product/feedback/' })
    const submittedSearchTerm = listSearch.searchTerm || ''
    const statusOption = listSearch.status
    const sortOption = listSearch.sort
    const tagFilter = listSearch.tag
    const [search, setSearch] = useState(submittedSearchTerm)
    const [tickets, setTickets] = useState<TicketListItem[] | null>(null)
    const [ticketCount, setTicketCount] = useState<number | null>(null)
    const [availableTags, setAvailableTags] = useState<string[]>([])
    const [ticketDialogOpen, setTicketDialogOpen] = useState(false)
    const requestVersionRef = useRef(0)
    const canCreateTicket = canCreateFeedbackContent()

    useEffect(() => {
        const abortController = new AbortController()

        void (async () => {
            const pageSize = 20
            const tags: string[] = []
            let count: number | null = null

            while (count === null || tags.length < count) {
                const result = await client.product.feedback.listTags.query(
                    {
                        offset: tags.length,
                        limit: pageSize,
                        returnCount: count === null,
                    },
                    { signal: abortController.signal }
                )
                tags.push(...result.tags)
                count ??= result.count

                if (result.tags.length === 0) {
                    break
                }
            }

            return tags
        })()
            .then((result) => {
                setAvailableTags(result)
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setAvailableTags([])
                }
            })

        return () => abortController.abort()
    }, [])

    useEffect(() => {
        const abortController = new AbortController()
        const requestVersion = requestVersionRef.current + 1
        requestVersionRef.current = requestVersion
        const status = statusOption === 'all' ? undefined : statusOption
        const excludeStatuses: Status[] | undefined = statusOption === 'all' ? ['done', 'rejected'] : undefined
        const untagged = tagFilter === TAG_FILTER_NONE
        const tag = tagFilter !== TAG_FILTER_ALL && tagFilter !== TAG_FILTER_NONE ? tagFilter : undefined

        setTickets(null)
        setTicketCount(null)

        void client.product.feedback.listTickets
            .query(
                {
                    limit: pageSize,
                    offset: (listSearch.page - 1) * pageSize,
                    returnCount: true,
                    sort: getTicketSort(sortOption),
                    excludeStatuses,
                    searchTerm: submittedSearchTerm || undefined,
                    status,
                    untagged: untagged || undefined,
                    tag,
                },
                { signal: abortController.signal }
            )
            .then((result) => {
                if (requestVersionRef.current !== requestVersion) {
                    return
                }

                setTickets(result.tickets)
                setTicketCount(result.count ?? result.tickets.length)
            })
            .catch(() => {
                if (!abortController.signal.aborted && requestVersionRef.current === requestVersion) {
                    setTickets([])
                    setTicketCount(0)
                }
            })

        return () => abortController.abort()
    }, [listSearch.page, statusOption, submittedSearchTerm, sortOption, tagFilter])

    useEffect(() => {
        setSearch(submittedSearchTerm)
    }, [submittedSearchTerm])

    const getListSearch = (overrides: Partial<Search> = {}): Search => {
        const nextSearch = { ...listSearch, ...overrides }

        return {
            ...nextSearch,
            searchTerm: nextSearch.searchTerm || undefined,
        }
    }

    const submitSearch = () => {
        const nextSearchTerm = search.trim()

        if (nextSearchTerm === submittedSearchTerm) {
            return
        }

        void navigate({
            to: '/product/feedback',
            search: getListSearch({ page: 1, searchTerm: nextSearchTerm }),
        })
    }

    const createTicket = async (input: {
        title: string
        message: string
        attachments: AttachmentInput
        tags?: string[]
    }) => {
        const ticket = await client.product.feedback.createTicket.mutate(input)
        await navigate({
            to: '/product/feedback/tickets/$ticketNumber',
            params: { ticketNumber: ticket.number.toString() },
            search: getListSearch(),
        })
    }

    return (
        <Page
            title={t('product.feedback.home.title')}
            description={t('product.feedback.home.description')}
            className="flex min-h-full flex-col space-y-6 pb-8"
        >
            <Toolbar
                search={search}
                setSearch={setSearch}
                submitSearch={submitSearch}
                statusOption={statusOption}
                sortOption={sortOption}
                tagFilter={tagFilter}
                availableTags={availableTags}
                ticketCount={ticketCount}
                canCreateTicket={canCreateTicket}
                setTicketDialogOpen={setTicketDialogOpen}
                getListSearch={getListSearch}
            />
            <TicketList
                tickets={tickets}
                ticketCount={ticketCount}
                submittedSearchTerm={submittedSearchTerm}
                canCreateTicket={canCreateTicket}
                setTicketDialogOpen={setTicketDialogOpen}
                getListSearch={getListSearch}
            />
            <TicketDialog open={ticketDialogOpen} onOpenChange={setTicketDialogOpen} onSubmit={createTicket} />
        </Page>
    )
})
